import React, { useEffect, useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  Pagination,
  FunctionField,
  useDataProvider,
  useRefresh,
  useGetIdentity,
  usePermissions,
  useListContext,
} from 'react-admin';
import { toast } from 'react-toastify';
import { DEFAULTS, MEDICAL_GROUPS_STRINGS } from '../../constants';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import EditMedicalGroupDialog from './EditMedicalGroupDialog';
import { MedicalGroupFormValues } from '../../utils/DataTypes';

const formatDate = (value: any) => {
  if (!value) return '';
  const timestamp = typeof value === 'string' ? parseInt(value, 10) : value;
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
};

const MedicalGroupsTable = () => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();

  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<any | null>(null);

  const initialFormState: MedicalGroupFormValues = {
    id: '',
    billing_client_number: '',
    billing_client_name: '',
    medical_group_number: '',
    medical_group_name: '',
    plan_location: '',
  };

  const [formValues, setFormValues] = useState(initialFormState);
  const [originalValues, setOriginalValues] = useState(initialFormState);

  const handleEdit = (record: any) => {
    setEditingRowId(record.client_medical_groups_key_id);

    const initialValues = {
      id: record.id,
      billing_client_number: record.billing_client_number || '',
      billing_client_name: record.billing_client_name || '',
      medical_group_number: record.medical_group_number || '',
      medical_group_name: record.medical_group_name || '',
      plan_location: record.plan_location || '',
    };

    setFormValues(initialValues);
    setOriginalValues(initialValues);
    setIsDialogOpen(true);
  };

  const getChangedFields = () => {
    const changedFields: Record<string, any> = {};

    Object.keys(formValues).forEach((key) => {
      const rawCurrent = formValues[key as keyof typeof formValues];
      const rawOriginal = originalValues[key as keyof typeof originalValues];

      const cleanCurrent = typeof rawCurrent === 'string' ? rawCurrent.trim() : rawCurrent;
      const cleanOriginal = typeof rawOriginal === 'string' ? rawOriginal.trim() : rawOriginal;

      if (cleanCurrent !== cleanOriginal) {
        changedFields[key] = cleanCurrent === null ? '' : cleanCurrent;
      }
    });

    return changedFields;
  };

  const handleSave = async () => {
    const rawChangedFields = getChangedFields();

    const trimmedChangedFields: Record<string, any> = {};
    Object.keys(rawChangedFields).forEach((key) => {
      const value = rawChangedFields[key];
      trimmedChangedFields[key] = typeof value === 'string' ? value.trim() : value;
    });

    if (Object.keys(trimmedChangedFields).length === 0) {
      toast.info(MEDICAL_GROUPS_STRINGS.TOAST.NO_CHANGES);
      handleDialogClose();
      return;
    }

    const payload = {
      client_medical_groups_key_id: editingRowId,
      last_modified_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
      ...trimmedChangedFields,
    };

    try {
      await dataProvider.update('MedicalGroupsCrosswalk', {
        id: editingRowId,
        data: payload,
        previousData: originalValues,
      });
      toast.success(MEDICAL_GROUPS_STRINGS.TOAST.SUCCESS);
      refresh();
    } catch (error) {
      toast.error(MEDICAL_GROUPS_STRINGS.TOAST.ERROR);
    }

    handleDialogClose();
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingRowId(null);
  };

  const handleDeleteClick = (record: any) => {
    setRecordToDelete(record);
  };

  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;

    try {
      await dataProvider.delete('SoftDeleteMedicalGroup', {
        id: recordToDelete.client_medical_groups_key_id,
        previousData: {
          id: recordToDelete.client_medical_groups_key_id,
          user: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        },
      });

      toast.success('Medical Group deleted successfully');
      refresh();
    } catch (error: any) {
      toast.error('Delete failed');
    }

    setRecordToDelete(null);
  };

  return (
    <>
      <List
        resource="MedicalGroupsCrosswalk"
        actions={false}
        pagination={<Pagination />}
        sort={{ field: 'billing_client_number', order: 'ASC' }}
        sx={{ width: '100%', '& .RaList-content': { boxShadow: 'none' } }}
      >
        <Datagrid
          bulkActionButtons={false}
          sx={{
            tableLayout: 'fixed',
            '& .MuiTableCell-head': {
              textAlign: 'center',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
            '& .MuiTableCell-body': {
              textAlign: 'center',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          }}
        >
          <TextField
            source="billing_client_name"
            label={MEDICAL_GROUPS_STRINGS.LABELS.BILLING_CLIENT_NAME}
            sortable={true}
          />
          <TextField
            source="billing_client_number"
            label={MEDICAL_GROUPS_STRINGS.LABELS.BILLING_CLIENT_NUMBER}
            sortable={true}
          />
          <TextField
            source="medical_group_number"
            label={MEDICAL_GROUPS_STRINGS.LABELS.MEDICAL_GROUP_NUMBER}
            sortable={true}
          />
          <TextField
            source="medical_group_name"
            label={MEDICAL_GROUPS_STRINGS.LABELS.MEDICAL_GROUP_NAME}
            sortable={true}
          />
          <TextField source="plan_location" label={MEDICAL_GROUPS_STRINGS.LABELS.PLAN_LOCATION} sortable={true} />
          <FunctionField
            key="created_ts"
            label={MEDICAL_GROUPS_STRINGS.LABELS.CREATED_ON}
            render={(record: any) => formatDate(record.created_ts)}
            sortable={false}
          />
          <TextField source="created_by_text" label={MEDICAL_GROUPS_STRINGS.LABELS.CREATED_BY} />
          <FunctionField
            key="last_modified_ts"
            label={MEDICAL_GROUPS_STRINGS.LABELS.MODIFIED_ON}
            render={(record: any) => formatDate(record.last_modified_ts)}
            sortable={false}
          />
          <TextField
            source="last_modified_by_text"
            label={MEDICAL_GROUPS_STRINGS.LABELS.MODIFIED_BY}
            sortable={false}
          />
          {permissions.adminPermission && (
            <FunctionField
              label={MEDICAL_GROUPS_STRINGS.LABELS.ACTIONS}
              render={(record: any) => (
                <Box display="flex" justifyContent="center" gap={1}>
                  <button
                    onClick={() => handleEdit(record)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <EditIcon style={{ color: '#00909A' }} />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(record)}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <DeleteIcon style={{ color: '#00909A' }} />
                  </button>
                </Box>
              )}
              sortable={false}
            />
          )}
        </Datagrid>
        {isDialogOpen && (
          <EditMedicalGroupDialog
            isOpen={isDialogOpen}
            formValues={formValues}
            originalValues={originalValues}
            setFormValues={setFormValues}
            onClose={handleDialogClose}
            onSave={handleSave}
          />
        )}
      </List>
      <Dialog open={!!recordToDelete} onClose={() => setRecordToDelete(null)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{recordToDelete?.medical_group_name || 'this medical group'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordToDelete(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="primary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MedicalGroupsTable;
